<template>
  <!--
    Dentro do passo a passo o bloco já é a etapa: o cabeçalho repetiria o
    título que a etapa acabou de dar, e o cartão desenharia uma moldura dentro
    da outra.
  -->
  <component :is="embutido ? 'div' : 'CCard'" v-if="permissoes.length || erro">
    <CCardHeader v-if="!embutido">
      Quem pode usar
      <small class="text-muted">— {{ permissoes.length }} permissão(ões)</small>
    </CCardHeader>

    <component :is="embutido ? 'div' : 'CCardBody'">
      <!--
        Falha de carga some da tela se o bloco inteiro depender da lista: o
        usuário concluiria que este conector não tem permissões a conceder — e
        numa integração cobrada por uso é a etapa de liberar a equipe certa.
      -->
      <CAlert v-if="erro" show color="danger" class="alerta-carga">
        <span>{{ erro }}</span>
        <CButton color="danger" variant="outline" size="sm" @click="carregar">
          Tentar novamente
        </CButton>
      </CAlert>

      <p v-if="permissoes.length" class="explicacao">
        São as mesmas permissões do módulo de Perfis, vistas daqui. Marcar nesta
        tela ou em Cadastros&nbsp;›&nbsp;Perfis dá no mesmo.
      </p>

      <div v-if="permissoes.length" class="tabela-rolagem">
        <table class="tabela-permissoes">
          <thead>
            <tr>
              <th class="coluna-perfil">Perfil</th>
              <th v-for="permissao in permissoes" :key="permissao.chave">
                <span class="contexto">{{ permissao.contexto }}</span>
                <span class="descricao">{{ permissao.descricao }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="perfil in perfis" :key="perfil.id" :class="{ 'perfil--inativo': !perfil.ativo }">
              <th scope="row" class="coluna-perfil">
                {{ perfil.descricao }}
                <span v-if="!perfil.ativo" class="marca-inativo">inativo</span>
              </th>
              <td v-for="permissao in permissoes" :key="permissao.chave" class="celula-check">
                <!--
                  O rótulo diz perfil e permissão: numa matriz, quem usa leitor
                  de tela ouviria só "caixa de seleção" e concederia acesso na
                  linha errada.
                -->
                <input
                  type="checkbox"
                  :aria-label="perfil.descricao + ' — ' + permissao.descricao"
                  :checked="perfil.concedidas.includes(permissao.chave)"
                  :disabled="salvando"
                  @change="alternar(perfil, permissao, $event.target.checked)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </component>
  </component>
</template>

<script>
/*
 * Matriz de perfis × permissões de uso de uma integração.
 *
 * Existe porque conceder isso pelo módulo de Perfis exige abrir perfil por
 * perfil e caçar a permissão certa entre dezenas de módulos — e quem acabou de
 * configurar a IA quer dizer, no mesmo lugar, quem pode usá-la. Não é uma lista
 * paralela: grava nas mesmas tabelas que o módulo de Perfis lê.
 *
 * Só aparece para conectores que declaram permissões de uso no manifesto.
 */
import { get, put } from '@/helpers/apiRequest';

export default {
  name: 'PermissoesUso',
  props: {
    slug: {
      type: String,
      required: true,
    },

    /* Dentro do passo a passo o cartão vira um bloco simples. */
    embutido: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      permissoes: [],
      perfis: [],
      salvando: false,
      erro: '',
    };
  },
  watch: {
    slug: 'carregar',
  },
  methods: {
    carregar() {
      if (!this.slug) {
        return;
      }

      this.erro = '';

      get(`/admin/integracoes/catalogo/${this.slug}/permissoes`)
        .then((resposta) => {
          this.aplicar(resposta);
          this.erro = '';
        })
        .catch((erro) => {
          this.permissoes = [];
          this.perfis = [];
          this.erro = (erro && erro.message)
            || 'Não foi possível carregar as permissões deste conector.';
        });
    },

    /*
     * Cada clique grava de imediato, sem botão de salvar: permissão é de efeito
     * amplo, e um "salvar" pendente esquecido na tela faria o usuário sair
     * achando que concedeu o acesso.
     */
    alternar(perfil, permissao, conceder) {
      this.salvando = true;

      put(`/admin/integracoes/catalogo/${this.slug}/permissoes`, {
        perfil_id: perfil.id,
        permissao: permissao.chave,
        conceder,
      })
        .then((resposta) => {
          this.aplicar(resposta);
          this.$emit('alterada');
        })
        .catch((erro) => {
          // Recarrega para a tela refletir o que de fato está gravado, em vez
          // de manter o check que o usuário acabou de marcar.
          this.carregar();
          this.$swal({
            title: 'Não foi possível alterar a permissão',
            text: erro.message,
            icon: 'error',
          });
        })
        .finally(() => {
          this.salvando = false;
        });
    },

    aplicar(resposta) {
      const dados = (resposta && resposta.data) || resposta || {};

      this.permissoes = dados.permissoes || [];
      this.perfis = dados.perfis || [];
    },
  },
  beforeMount() {
    this.carregar();
  },
};
</script>

<style scoped>
.alerta-carga {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.explicacao {
  margin: 0 0 14px;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

/* A matriz cresce em largura conforme o conector declara mais permissões. */
.tabela-rolagem {
  overflow-x: auto;
}

.tabela-permissoes {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.tabela-permissoes th,
.tabela-permissoes td {
  padding: 8px 10px;
  border-bottom: 1px solid #e9ecef;
  text-align: center;
  vertical-align: middle;
}

.tabela-permissoes thead th {
  border-bottom: 1px solid #d0d5dd;
  vertical-align: bottom;
  white-space: nowrap;
}

.coluna-perfil {
  text-align: left;
  white-space: nowrap;
  color: #344054;
  font-weight: 500;
  position: sticky;
  left: 0;
  background: #fff;
}

.contexto {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #2298a3;
}

.descricao {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.celula-check input {
  cursor: pointer;
}

.perfil--inativo {
  opacity: 0.6;
}

.marca-inativo {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f2f4f7;
  color: #667085;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
</style>
